import { createContext, useContext, ReactNode, useState } from 'react';
import { useRecoilState } from 'recoil';
import {
  facialValidationActiveState,
  registeredBiometriesState,
  userInfoCompletedState,
  userLastNameState,
  userNameState,
  voiceValidationActiveState,
  widgetTypes,
} from '../states';
import { useFacialAuth } from './FacialProvider';
import { useVocalAuth } from './VocalProvider';

type IProps = {
  children: ReactNode;
};

type ContextProps = {
  activeFacial: boolean;
  activeVocal: boolean;
  registeredBiometries: widgetTypes[];
  noBiometrySelected: boolean;
  userName?: string;
  setUserName(userName?: string): void;
  userLastname?: string;
  setUserLastname(userLastname?: string): void;
  userInfoCompleted: boolean;
  setUserInfoCompleted(value: boolean): void;
  onAddRegisteredBiometry(biometry: widgetTypes): void;
  onRemoveRegisteredBiometry(biometry: widgetTypes): void;
  setActiveVocal(active: boolean): void;
  setActiveFacial(active: boolean): void;
  checkFacial: boolean;
  checkVocal: boolean;
  customPhraseValid: boolean;
  setCustomPhraseValid(value: boolean): void;
};

const Ctx = createContext<ContextProps>({
  activeFacial: true,
  setActiveFacial: () => {},
  activeVocal: false,
  setActiveVocal: () => {},
  registeredBiometries: [],
  noBiometrySelected: false,
  onAddRegisteredBiometry: () => {},
  onRemoveRegisteredBiometry: () => {},
  checkFacial: false,
  checkVocal: false,
  userName: '',
  setUserName: () => {},
  userLastname: '',
  setUserLastname: () => {},
  userInfoCompleted: false,
  setUserInfoCompleted: () => {},
  customPhraseValid: false,
  setCustomPhraseValid: () => {},
});

export const ConfigProvier = ({ children }: IProps) => {
  const { facialValidated } = useFacialAuth();
  const { vocalValidated } = useVocalAuth();
  const [activeFacial, setActiveFacial] = useRecoilState<boolean>(
    facialValidationActiveState
  );
  const [activeVocal, setActiveVocal] = useRecoilState<boolean>(
    voiceValidationActiveState
  );
  const [registeredBiometries, setRegisteredBiometries] = useRecoilState(
    registeredBiometriesState
  );
  const [userName, setUserName] = useRecoilState(userNameState);
  const [userLastname, setUserLastname] = useRecoilState(userLastNameState);
  const [userInfoCompleted, setUserInfoCompleted] = useRecoilState(
    userInfoCompletedState
  );

  const [customPhraseValid, setCustomPhraseValid] = useState<boolean>(false);
  const checkFacial = activeFacial && facialValidated;
  const checkVocal = activeVocal && vocalValidated;
  const noBiometrySelected = !checkFacial && !checkVocal;

  const onAddRegisteredBiometry = (biometry: widgetTypes) => {
    if (!registeredBiometries.includes(biometry)) {
      setRegisteredBiometries((prevState) => [...prevState, biometry]);
    }
  };

  const onRemoveRegisteredBiometry = (biometry: widgetTypes) => {
    if (registeredBiometries.includes(biometry)) {
      const updatedBiometries = registeredBiometries.filter(
        (item) => item !== biometry
      );
      setRegisteredBiometries(updatedBiometries);
    }
  };

  return (
    <Ctx.Provider
      value={{
        activeFacial,
        setActiveFacial,
        activeVocal,
        setActiveVocal,
        userName,
        setUserName,
        userLastname,
        setUserLastname,
        userInfoCompleted,
        setUserInfoCompleted,
        registeredBiometries,
        noBiometrySelected,
        checkFacial,
        checkVocal,
        onAddRegisteredBiometry,
        onRemoveRegisteredBiometry,
        customPhraseValid,
        setCustomPhraseValid,
      }}
    >
      {children}
    </Ctx.Provider>
  );
};

export const useConfig = () => useContext(Ctx);
